import React, { useCallback, useMemo } from "react";

import RemoveCircleOutlineOutlinedIcon from "@mui/icons-material/RemoveCircleOutlineOutlined";
import { Stack } from "@mui/material";

import useGetExcludedAssets from "@fartherfinance/frontend/api/Investment/hooks/useGetExcludedAssets";
import useUpdatePortfolioPlanAssignment from "@fartherfinance/frontend/api/Investment/hooks/useUpdatePortfolioPlanAssignment";
import {
  ConcreteImplementationPlanStatus,
  TransformerId,
} from "@fartherfinance/frontend/api/Investment/types";
import {
  ClientId,
  FartherAccountId,
  PortfolioPlanAssignmentId,
  PortfolioPlanId,
} from "@fartherfinance/frontend/api/Types";

import {
  EQUIVALENT_SECURITIES_TOOLTIP_TEXT,
  IS_LIQUIDATING_TOOLTIP_TEXT,
  TAX_LOSS_HARVESTING_TOOLTIP_TEXT,
} from "../const";
import useRequestAuth from "@src/multiCustodian/hooks/useRequestAuth";
import useStatusNotification from "@src/multiCustodian/hooks/useStatusNotification";
import LinkButton from "@src/sharedComponents/LinkButton/LinkButton";
import Select from "@src/sharedComponents/Select/Select";
import Skeleton from "@src/sharedComponents/Skeleton/Skeleton";
import Switch from "@src/sharedComponents/Switch/Switch";
import Tooltip from "@src/sharedComponents/Tooltip/Tooltip";
import TooltipBasic from "@src/sharedComponents/Tooltip/TooltipBasic";
import Text from "@src/sharedComponents/Typography/Text";

import DoNotSellButtonV2 from "./DoNotSellButtonV2";
import EditableTradingGroupNameV2 from "./EditableTradingGroupNameV2";
import DoNotSellWarningModal from "./modals/DoNotSellWarningModal";
import EnableTaxLossHarvestingModalV2 from "./modals/EnableTaxLossHarvestingModalV2";
import EquivalentSecuritiesModalV2 from "./modals/EquivalentSecuritiesModalV2";
import RemoveAccountModalV2 from "./modals/RemoveAccountModalV2";
import TradingModalV2 from "./modals/TradingModalV2";

import styles from "./TradingGroupsTable.module.css";

type AssignedAccount = {
  fartherAccountId: FartherAccountId;
  accountName: string;
};

type Rebalancers = ("AlphaRebalancer" | "TaxLossHarvestingOnlyRebalancer")[];

export type TradingTableGroupsData = {
  assignmentId: PortfolioPlanAssignmentId;
  accounts: AssignedAccount[];
  intent: "TradingOn" | "TradingOff";
  status: ConcreteImplementationPlanStatus;
  rebalancers: Rebalancers;
  tradingGroupName: string;
  transformers: TransformerId[];
};

type TradingGroupsProps = TradingTableGroupsData & {
  portfolioPlanId: PortfolioPlanId;
  clientId: ClientId;
};

type ModalOptions =
  | { type: "TradingModal" }
  | { type: "TLHModal" }
  | { type: "EquivalentSecuritiesModal" }
  | { type: "RemoveAccountModal"; accountId: FartherAccountId }
  | {
      type: "DoNotSellWarningModal";
      accountId: FartherAccountId;
      securityCount: number;
    }
  | null;

// FIX: We should get it from the transformer list endpoint in the future
export const EquivalentSecurityTransformerId =
  TransformerId.parse("IPT_FARF-EQEQ-0000");

const TradingTableGroupsTableV2 = (props: TradingGroupsProps) => {
  const [modalOpen, setModalOpen] = React.useState<ModalOptions>(null);

  const [isMutating, setIsMutating] = React.useState(false);
  const notify = useStatusNotification();

  const auth = useRequestAuth();
  const updatePortfolioAssignment = useUpdatePortfolioPlanAssignment(auth);
  const excludedAssets = useGetExcludedAssets(
    props.portfolioPlanId,
    props.assignmentId,
    auth
  );

  const [editingNameInput, setEditingNameInput] = React.useState("");

  const checkForDoNotSellSecurities = useCallback(() => {
    // If the data is still loading or if there's no data, we can't check
    if (excludedAssets.isLoading || !excludedAssets.data) {
      return 0;
    }

    // Return the count of excluded assets (Do Not Sell securities)
    return excludedAssets.data.length;
  }, [excludedAssets.data, excludedAssets.isLoading]);

  const handleRemoveAccount = useCallback(
    (account: FartherAccountId) => async () => {
      setIsMutating(true);

      const newAccounts = props.accounts
        .filter((acc) => acc.fartherAccountId !== account)
        .map((acc) => acc.fartherAccountId);

      try {
        await updatePortfolioAssignment({
          abstractPortfolioId: props.portfolioPlanId,
          concretePortfolioId: props.assignmentId,
          accounts: newAccounts,
        });

        notify("Account removed from trading group", "Success");
      } catch {
        notify("Error removing account from trading group", "Error");
      } finally {
        setIsMutating(false);
        setModalOpen(null);
      }
    },
    [
      notify,
      props.accounts,
      props.assignmentId,
      props.portfolioPlanId,
      updatePortfolioAssignment,
    ]
  );

  const isTLHEnabled = useMemo(
    () =>
      props.rebalancers.some(
        (reb) => reb === "TaxLossHarvestingOnlyRebalancer"
      ),
    [props.rebalancers]
  );

  const toggleTLHSwitch = useCallback(
    (isTLHOn: boolean) => async () => {
      setIsMutating(true);

      const newRebalancers = isTLHOn
        ? props.rebalancers.filter(
            (reb) => reb !== "TaxLossHarvestingOnlyRebalancer"
          )
        : ([...props.rebalancers, "TaxLossHarvestingOnlyRebalancer"] as const);

      try {
        await updatePortfolioAssignment({
          abstractPortfolioId: props.portfolioPlanId,
          concretePortfolioId: props.assignmentId,
          rebalancers: newRebalancers.map((reb) => ({ rebalancer: reb })),
        });
        notify("Tax-Loss Harvesting setting successfully changed", "Success");
      } catch {
        notify("Error changing Tax-Loss Harvesting setting", "Error");
      } finally {
        setIsMutating(false);
        setModalOpen(null);
      }
    },
    [notify, props, updatePortfolioAssignment]
  );

  const toggleTradingSwitch = useCallback(
    (isTradingOn: boolean) => async () => {
      setIsMutating(true);

      try {
        await updatePortfolioAssignment({
          abstractPortfolioId: props.portfolioPlanId,
          concretePortfolioId: props.assignmentId,
          intent: isTradingOn ? "TradingOff" : "TradingOn",
        });
        notify("Trading setting successfully changed", "Success");
      } catch {
        notify("Error updating Trading", "Error");
      } finally {
        setIsMutating(false);
        setModalOpen(null);
      }
    },
    [
      notify,
      props.assignmentId,
      props.portfolioPlanId,
      updatePortfolioAssignment,
    ]
  );

  const isEquivalentSecurityEnabled = useMemo(() => {
    return props.transformers.some(
      (transformer) => transformer === EquivalentSecurityTransformerId
    );
  }, [props.transformers]);

  const updateInputName = useCallback(
    async (inputName: string) => {
      try {
        setIsMutating(true);
        await updatePortfolioAssignment({
          abstractPortfolioId: props.portfolioPlanId,
          concretePortfolioId: props.assignmentId,
          name: inputName,
        });

        notify("Trading name successfully changed", "Success");
      } catch {
        notify("Error updating trading name", "Error");
      } finally {
        setIsMutating(false);
      }
    },
    [
      notify,
      props.assignmentId,
      props.portfolioPlanId,
      updatePortfolioAssignment,
    ]
  );

  const toggleEquivalentSecurities = useCallback(
    (isEquivalentSecuritiesOn: boolean) => async () => {
      setIsMutating(true);

      try {
        const newTransformers = isEquivalentSecuritiesOn
          ? props.transformers.filter(
              (trans) => trans !== EquivalentSecurityTransformerId
            )
          : [...props.transformers, EquivalentSecurityTransformerId];

        const dataToSend = newTransformers.map((transformer) => ({
          transformerId: transformer,
          metadata: null,
        }));

        await updatePortfolioAssignment({
          abstractPortfolioId: props.portfolioPlanId,
          concretePortfolioId: props.assignmentId,
          transformers: dataToSend,
        });

        notify("Trading setting successfully changed", "Success");
      } catch {
        notify("Error updating Trading", "Error");
      } finally {
        setIsMutating(false);
        setModalOpen(null);
      }
    },
    [
      notify,
      props.assignmentId,
      props.portfolioPlanId,
      props.transformers,
      updatePortfolioAssignment,
    ]
  );

  const setModal = useCallback(
    (modalType: ModalOptions) => () => setModalOpen(modalType),
    []
  );

  const handleAccountRemoveClick = useCallback(
    (account: FartherAccountId) => () => {
      // Check if there are DNS securities
      const securityCount = checkForDoNotSellSecurities();
      if (securityCount > 0) {
        // Show DNS warning modal
        setModalOpen({
          type: "DoNotSellWarningModal",
          accountId: account,
          securityCount: securityCount,
        });
        return;
      }

      // Show regular removal modal
      setModalOpen({
        type: "RemoveAccountModal",
        accountId: account,
      });
    },
    [checkForDoNotSellSecurities]
  );

  if (props.accounts.length === 0) {
    return (
      <EmptyComponent
        accountName={props.tradingGroupName}
        isMutating={isMutating}
        title="No Accounts Assigned"
      />
    );
  }

  const accountsNumber = props.accounts.length;
  const accountPluralized = `Account${accountsNumber === 1 ? "" : "s"}`;
  const title = `${accountsNumber} ${accountPluralized}`;

  const isLiquidating =
    props.status === "Liquidating" ||
    props.status == "FullyLiquidating" ||
    props.status == "Frozen_PendingLiquidationProposalApproval" ||
    props.status == "Frozen_PendingLiquidationTaskCompletion";

  return (
    <div className={styles.container}>
      <div className={styles.listHeaderDiv}>
        <div className={styles.flexDiv}>
          <EditableTradingGroupNameV2
            assignmentGroupName={props.tradingGroupName}
            saveNewTradingGroupName={updateInputName}
            setEditingNameInput={setEditingNameInput}
            inputValue={editingNameInput}
            isMutating={isMutating}
          />

          {isMutating ? (
            <Skeleton width={100} />
          ) : (
            <p className={styles.secondaryHeaderText}>{title}</p>
          )}
        </div>
        <Select
          disabled={true || isMutating}
          value={"Add Accounts"}
          options={[""]}
          onChange={() => {}}
          style={{ width: 200 }}
          renderValue={(value) => value}
        />
      </div>

      <div className={styles.optionsDiv}>
        <div className={styles.half_width}>
          <p className={styles.headingText}>{accountPluralized}</p>
        </div>

        <div className={styles.divRight}>
          <Stack flex={1} spacing={"8px"}>
            {props.accounts.map((account) => (
              <Stack
                key={account.fartherAccountId}
                width={"full"}
                direction={"row"}
                justifyContent={"space-between"}
              >
                {isMutating ? (
                  <Skeleton width={120} />
                ) : (
                  <Text variant="body">{account.accountName}</Text>
                )}
                <RemoveCircleOutlineOutlinedIcon
                  className={styles.removeIcon}
                  onClick={handleAccountRemoveClick(account.fartherAccountId)}
                  style={
                    isMutating ? { opacity: 0.5, cursor: "default" } : undefined
                  }
                />
                <RemoveAccountModalV2
                  tradingGroupName={props.tradingGroupName}
                  accountName={account.accountName}
                  closeModal={setModal(null)}
                  isOpen={
                    modalOpen?.type === "RemoveAccountModal" &&
                    modalOpen.accountId === account.fartherAccountId
                  }
                  onContinue={handleRemoveAccount(account.fartherAccountId)}
                  isMutating={isMutating}
                  type="remove"
                />
                <DoNotSellWarningModal
                  tradingGroupName={props.tradingGroupName}
                  accountName={account.accountName}
                  closeModal={setModal(null)}
                  isOpen={
                    modalOpen?.type === "DoNotSellWarningModal" &&
                    modalOpen.accountId === account.fartherAccountId
                  }
                  onContinue={handleRemoveAccount(account.fartherAccountId)}
                  isMutating={isMutating}
                  securityCount={
                    modalOpen?.type === "DoNotSellWarningModal"
                      ? modalOpen.securityCount
                      : 0
                  }
                />
              </Stack>
            ))}
          </Stack>
        </div>
      </div>

      <div className={styles.settingsDiv}>
        <div className={styles.half_width}>
          <p className={styles.headingText}>Trading group Settings</p>
        </div>

        <div className={styles.half_width}>
          <TradeOptions
            enabled={!isMutating && !isLiquidating}
            optionTitle={"Trading"}
            isSwitchOn={props.intent === "TradingOn"}
            onSwitch={setModal({ type: "TradingModal" })}
            tooltipText={
              isLiquidating ? IS_LIQUIDATING_TOOLTIP_TEXT : undefined
            }
            isLiquidating={isLiquidating}
          />

          <TradingModalV2
            isCurrentlyTrading={props.intent === "TradingOn"}
            isOpen={modalOpen?.type === "TradingModal"}
            closeModal={setModal(null)}
            onConfirm={toggleTradingSwitch(props.intent === "TradingOn")}
            isMutating={isMutating}
          />

          <TradeOptions
            enabled={!isMutating && !isLiquidating}
            optionTitle={"Tax-loss harvesting"}
            isSwitchOn={isTLHEnabled}
            onSwitch={
              isTLHEnabled
                ? toggleTLHSwitch(isTLHEnabled)
                : setModal({ type: "TLHModal" })
            }
            tooltipText={TAX_LOSS_HARVESTING_TOOLTIP_TEXT}
            isLiquidating={isLiquidating}
          />

          <EnableTaxLossHarvestingModalV2
            isOpen={modalOpen?.type === "TLHModal"}
            closeModal={setModal(null)}
            onConfirm={toggleTLHSwitch(isTLHEnabled)}
            isMutating={isMutating}
          />

          <TradeOptions
            enabled={!isMutating && !isLiquidating}
            optionTitle={"Equivalent Securities"}
            isSwitchOn={isEquivalentSecurityEnabled}
            tooltipText={EQUIVALENT_SECURITIES_TOOLTIP_TEXT}
            linkTo={`/Client/${props.clientId}/Investments/PortfoliosV2/${props.portfolioPlanId}/Accounts/EquivalentSecurities`}
            onSwitch={
              !isEquivalentSecurityEnabled
                ? setModal({ type: "EquivalentSecuritiesModal" })
                : toggleEquivalentSecurities(isEquivalentSecurityEnabled)
            }
            isLiquidating={isLiquidating}
          />

          <EquivalentSecuritiesModalV2
            isOpen={modalOpen?.type === "EquivalentSecuritiesModal"}
            closeModal={setModal(null)}
            isMutating={isMutating}
            onConfirm={toggleEquivalentSecurities(isEquivalentSecurityEnabled)}
          />
        </div>
      </div>

      <DoNotSellButtonV2
        clientId={props.clientId}
        portfolioPlanId={props.portfolioPlanId}
        assignmentId={props.assignmentId}
      />
    </div>
  );
};

export const ErrorTradingTableGroups = () => (
  <Text style={{ paddingBottom: 32 }} color="textSubtle" variant="body">
    Error loading trading groups
  </Text>
);

type TradeOptionsProps = {
  enabled: boolean;
  optionTitle: string;
  isSwitchOn: boolean;
  onSwitch: (currentState: boolean) => void;
  tooltipText?: string;
  linkTo?: string;
  isLiquidating: boolean;
};

const TradeOptions = ({
  enabled,
  optionTitle,
  isSwitchOn,
  onSwitch,
  tooltipText,
  linkTo,
  isLiquidating,
}: TradeOptionsProps) => {
  return (
    <div className={styles.flexApart}>
      <div className={styles.flexDiv}>
        {linkTo ? (
          <LinkButton text={optionTitle} isCustom={true} to={linkTo} />
        ) : (
          <p className={enabled ? styles.text : styles.textDisabled}>
            {optionTitle}
          </p>
        )}

        {tooltipText ? (
          <TooltipBasic
            type={enabled ? "primary" : "secondary"}
            text={tooltipText}
          />
        ) : null}
      </div>

      {isLiquidating ? (
        <Tooltip tooltipText={IS_LIQUIDATING_TOOLTIP_TEXT} placement={"left"}>
          <div>
            <Switch
              disabled={!enabled}
              on={isSwitchOn}
              onClick={() => onSwitch?.(isSwitchOn)}
            />
          </div>
        </Tooltip>
      ) : (
        <Switch
          disabled={!enabled}
          on={isSwitchOn}
          onClick={() => onSwitch?.(isSwitchOn)}
        />
      )}
    </div>
  );
};

export default TradingTableGroupsTableV2;

const EmptyComponent = ({
  title,
  isMutating,
  accountName,
}: {
  title: string;
  isMutating: boolean;
  accountName: string;
}) => (
  <div className={styles.listHeaderDiv} style={{ marginBottom: 40 }}>
    <div className={styles.flexDiv}>
      <Text>{accountName}</Text>
      {isMutating ? (
        <Skeleton width={100} />
      ) : (
        <p className={styles.secondaryHeaderText}>{title}</p>
      )}
    </div>

    <Select
      disabled={true || isMutating}
      value={"Add Accounts"}
      options={[""]}
      onChange={() => {}}
      style={{ width: 200 }}
      renderValue={(value) => value}
    />
  </div>
);
